import { Typography, Breadcrumb, Spin, Button } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Head, router } from '@inertiajs/react';

import AdminLayout from '../../../components/layouts/AdminLayout';
import EventForm from './components/EventForm';
import { api } from '../../../lib/apiClient';
import { keys } from '../../../lib/queryKeys';
import { EventOwner } from '../event_owners/index.page';
import { Category } from '@/types/events';

const { Title } = Typography;

interface FormDataResponse {
  categories: Category[];
  event_owners: EventOwner[];
}

export default function NewEvent({ errors }: { errors?: Record<string, string[]> }) {

  // Fetch categories and event owners from the backend
  const formDataQuery = useQuery({
    queryKey: keys.events.formData.new(),
    queryFn: () => api.get<FormDataResponse>('/admin/events/new'),
    enabled: true,
  });


  if (formDataQuery.isLoading) {
    return (
      <AdminLayout>
        <div className="w-full h-screen flex items-center justify-center">
          <Spin size="large" />
        </div>
      </AdminLayout>
    );
  }

  if (formDataQuery.isError) {
    return (
      <AdminLayout>
        <div className="w-full h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-500 text-lg mb-4">Error loading form data</p>
            <Button onClick={() => router.visit('/admin/events')}>
              Back to Events
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const categories = formDataQuery.data?.categories || [];
  const eventOwners = formDataQuery.data?.event_owners || [];

  return (
    <AdminLayout>
      <Head title="New Event" />
      <div className="w-full bg-white">
        {/* Header section */}
        <div className="bg-white flex justify-between items-center px-6 py-4 border-b border-gray-200">
          <div>
            <Breadcrumb
              items={[
                { title: <a onClick={() => router.visit('/admin/events')}>List of Events</a> },
                { title: 'Create New Event' },
              ]}
              className="mb-2"
            />
            <Title level={4} className="!mb-0">Create New Event</Title>
          </div>

        </div>

        {/* Form section */}
        <div className="pt-6">
          {/* Display validation errors if they exist */}
          {errors && Object.keys(errors).length > 0 && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded">
              <h3 className="text-red-800 font-medium mb-2">Please fix the following errors:</h3>
              <ul className="list-disc list-inside text-red-600">
                {Object.entries(errors).map(([field, messages]) => (
                  messages.map((message, idx) => (
                    <li key={`${field}-${idx}`}>{field}: {message}</li>
                  ))
                ))}
              </ul>
            </div>
          )}
          <EventForm
            categories={categories}
            eventOwners={eventOwners}
            action="/admin/events"
            method="post"
          />
        </div>
      </div>
    </AdminLayout>
  );
}
